import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    CircularProgress,
    TableContainer,
    Button,
    Typography,
    Checkbox,
    ButtonGroup
} from '@material-ui/core';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { Alert } from '@material-ui/lab';
import TablePager from '../../components/atom/TablePager';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import SortableTableCell from '../../components/atom/SortableTableCell';
import { useSorting } from '../../hooks/useSorting';
import FilterLine from '../../components/filter/FilterLine';
import { useModals, MODAL_TYPE } from '../../hooks/useModal';
import {
    BOOKINGS_LIST_PAGE_FILTERS,
    getFilterFromUrl,
    getUrlFromFilter
} from './BookingsListFilter';
import BookingItemTableRow from './BookingItemTableRow';
import { normalizeFilters } from '../../utils/filters';

function BookingsListPage({ customer, loginAsCustomerWithId, cartItemCount }) {
    const history = useHistory();
    const location = useLocation();
    const { showModal } = useModals();
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [currentFilters, setFilters] = useState(getFilterFromUrl(location.search));
    const { sort, sortDir, setSorting } = useSorting(['id', 'DESC']);

    const [bulkSelecting, setBulkSelecting] = useState(false);
    const [selectedBookingIds, setSelectedBookingIds] = useState(new Set());

    const setCurrentFilters = useCallback(
        newfilters => history.replace(getUrlFromFilter(newfilters)),
        [history]
    );

    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
    }, [location.search, setFilters]);

    const [data, loading, error, , reloadData] = useSwaggerClient(
        'bookings',
        'getBookingItemsForClients',
        {
            page,
            pageSize,
            confirmationCode: currentFilters.confirmationCode,
            bookingId: currentFilters.bookingId,
            agentList: currentFilters.agent,
            vendorList: currentFilters.vendor,
            activityList: currentFilters.activity,
            guestList: currentFilters.guest,
            fromDate: moment(currentFilters.checkInDate).isValid()
                ? moment(currentFilters.checkInDate).format('YYYY-MM-DD')
                : '',
            toDate: moment(currentFilters.checkOutDate).isValid()
                ? moment(currentFilters.checkOutDate).format('YYYY-MM-DD')
                : '',
            fromCreateDate: moment(currentFilters.fromCreateDate).isValid()
                ? moment(currentFilters.fromCreateDate).format('YYYY-MM-DD')
                : '',
            toCreateDate: moment(currentFilters.toCreateDate).isValid()
                ? moment(currentFilters.toCreateDate).format('YYYY-MM-DD')
                : '',
            startDateTime: moment(currentFilters.startDateTime).isValid()
                ? moment(currentFilters.startDateTime).format('YYYY-MM-DD HH:mm:ss')
                : '',
            sort,
            sortDir,
            paymentType: currentFilters.paymentType,
            fromModifiedDate: moment(currentFilters.fromModifiedDate).isValid()
                ? moment(currentFilters.fromModifiedDate).format('YYYY-MM-DD')
                : '',
            toModifiedDate: moment(currentFilters.toModifiedDate).isValid()
                ? moment(currentFilters.toModifiedDate).format('YYYY-MM-DD')
                : '',
            statusList:
                Array.isArray(currentFilters.statusList) && currentFilters.statusList.length
                    ? currentFilters.statusList
                    : null
        }
    );

    const resetFilters = () =>
        setCurrentFilters({
            checkInDate: null,
            checkOutDate: null,
            fromCreateDate: null,
            toCreateDate: null,
            startDateTime: null,
            confirmationCode: '',
            vendor: null,
            agent: null,
            bookingId: '',
            activity: null,
            guest: null,
            paymentType: null,
            fromModifiedDate: null,
            toModifiedDate: null,
            statusList: []
        });

    useEffect(() => {
        if (loading) {
            return;
        }

        if (data?.booking_items.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading, setPage]);

    const selectableItems = (data?.booking_items || []).filter(
        item =>
            item.amount > 0 && item.status !== 'cancelled_full_refund' && item.status !== 'denied'
    );

    // Bulk selection handlers
    const handleSelectAll = useCallback(
        event => {
            if (event.target.checked) {
                setSelectedBookingIds(new Set(selectableItems.map(item => item.id)));
            } else {
                setSelectedBookingIds(new Set());
            }
        },
        [selectableItems]
    );

    const handleSelectBooking = useCallback(bookingId => {
        setSelectedBookingIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(bookingId)) {
                newSet.delete(bookingId);
            } else {
                newSet.add(bookingId);
            }
            return newSet;
        });
    }, []);

    const startBulkMode = useCallback(() => {
        setBulkSelecting(true);
        setSelectedBookingIds(new Set());
    }, []);

    const exitBulkMode = useCallback(() => {
        setBulkSelecting(false);
        setSelectedBookingIds(new Set());
    }, []);

    const getSelectedBookings = useCallback(() => {
        return data?.booking_items?.filter(item => selectedBookingIds.has(item.id)) || [];
    }, [selectedBookingIds, data?.booking_items]);

    const openBulkActionModal = useCallback(() => {
        if (selectedBookingIds.size > 0) {
            showModal(MODAL_TYPE.BULK_REFUND_CANCEL, {
                bookingItems: getSelectedBookings(),
                onFinished: () => {
                    exitBulkMode();
                    reloadData();
                }
            });
        }
    }, [selectedBookingIds, getSelectedBookings, showModal, reloadData, exitBulkMode]);

    const openSingleCancellationModal = useCallback(
        bookingItem => {
            showModal(MODAL_TYPE.CANCEL_BOOKINGITEM, {
                bookingItem,
                onCancelFinished: () => {
                    reloadData();
                }
            });
        },
        [showModal, reloadData]
    );

    const isAllSelectableSelected =
        selectableItems.length > 0 &&
        selectableItems.every(item => selectedBookingIds.has(item.id));

    const isIndeterminate = selectedBookingIds.size > 0 && !isAllSelectableSelected;

    const normalizedFilters = normalizeFilters(currentFilters);

    return (
        <Grid container className="golf-reservation">
            <Grid item xs={12}>
                <FilterLine
                    possibleFilters={BOOKINGS_LIST_PAGE_FILTERS}
                    filters={normalizedFilters}
                    setFilters={setCurrentFilters}
                    defaultValues={[
                        {
                            key: 'bookingId',
                            label: 'Reservation ID',
                            type: 'text',
                            value: ''
                        }
                    ]}
                    resetFilters={resetFilters}
                />
                {loading && data?.booking_items.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <>
                        <TableContainer style={{ overflowX: 'auto' }}>
                            <Table
                                className="mt-2"
                                style={{ backgroundColor: 'white' }}
                                size="small"
                            >
                                <TableHead>
                                    <TableRow>
                                        {bulkSelecting && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    indeterminate={isIndeterminate}
                                                    checked={isAllSelectableSelected}
                                                    onChange={handleSelectAll}
                                                    disabled={selectableItems.length === 0}
                                                />
                                            </TableCell>
                                        )}
                                        <SortableTableCell
                                            arrow={
                                                sort === 'bookings.users.firstname' ? sortDir : ''
                                            }
                                            onClick={() => setSorting('bookings.users.firstname')}
                                        >
                                            Guest
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'agent' ? sortDir : ''}
                                            onClick={() => setSorting('agent')}
                                        >
                                            Agent
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={
                                                sort === 'activities.companies.name' ? sortDir : ''
                                            }
                                            onClick={() => setSorting('activities.companies.name')}
                                        >
                                            Vendor
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'id' ? sortDir : ''}
                                            onClick={() => setSorting('id')}
                                        >
                                            Reservation ID
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'activities.name' ? sortDir : ''}
                                            onClick={() => setSorting('activities.name')}
                                        >
                                            Activity name
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'created_at' ? sortDir : ''}
                                            onClick={() => setSorting('created_at')}
                                        >
                                            Booked on
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'activity_start' ? sortDir : ''}
                                            onClick={() => setSorting('activity_start')}
                                        >
                                            Activity start time
                                        </SortableTableCell>
                                        {/* we search in a stored JSON,
                                    can we sort by an attribute of that stored JSON? */}
                                        <TableCell>Confirmation code</TableCell>
                                        <SortableTableCell
                                            arrow={sort === 'status' ? sortDir : ''}
                                            onClick={() => setSorting('status')}
                                        >
                                            Status
                                        </SortableTableCell>
                                        <TableCell align="right">
                                            {!bulkSelecting ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={startBulkMode}
                                                >
                                                    Bulk actions
                                                </Button>
                                            ) : (
                                                <ButtonGroup size="small" color="primary">
                                                    <Button
                                                        variant="contained"
                                                        onClick={openBulkActionModal}
                                                        disabled={selectedBookingIds.size === 0}
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        Continue ({selectedBookingIds.size})
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={exitBulkMode}
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </ButtonGroup>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {error && (
                                        <TableRow>
                                            <TableCell colSpan={11} style={{ textAlign: 'center' }}>
                                                <Alert severity="error">
                                                    <h4>
                                                        {error.response.body.message ||
                                                            error.message}
                                                    </h4>
                                                </Alert>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {data &&
                                        data.booking_items &&
                                        data.booking_items.map(booking_item => (
                                            <BookingItemTableRow
                                                key={booking_item.id}
                                                booking_item={booking_item}
                                                bulkSelecting={bulkSelecting}
                                                isSelected={selectedBookingIds.has(booking_item.id)}
                                                handleSelectBooking={handleSelectBooking}
                                                openSingleCancellationModal={
                                                    openSingleCancellationModal
                                                }
                                                loginAsCustomerWithId={loginAsCustomerWithId}
                                                showModal={showModal}
                                                customer={customer}
                                                cartItemCount={cartItemCount}
                                            />
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Grid
                            style={{
                                display: 'flex',
                                gap: '20px',
                                paddingTop: '12px',
                                paddingLeft: '12px'
                            }}
                        >
                            <Link to="/client/custombookings" target="_blank">
                                <Typography style={{ cursor: 'pointer' }} color="primary">
                                    View custom bookings
                                </Typography>
                            </Link>
                            <Link to="/client/expiredunconfirmedbookings" target="_blank">
                                <Typography style={{ cursor: 'pointer' }} color="primary">
                                    View expired unconfirmed bookings
                                </Typography>
                            </Link>
                        </Grid>
                        <Grid container justifyContent="flex-end">
                            <TablePager
                                total={data?.total}
                                page={page}
                                setPage={setPage}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );
}

export default BookingsListPage;
